import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Database, TrendingUp, Globe, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const TRAITS = [
  { key: "narcissism", label: "Narcissism" },
  { key: "machiavellianism", label: "Machiavellianism" },
  { key: "psychopathy", label: "Psychopathy" },
];
const BIG_FIVE = [
  { key: "extraversion", label: "Extraversion" },
  { key: "agreeableness", label: "Agreeableness" },
  { key: "conscientiousness", label: "Conscientiousness" },
  { key: "neuroticism", label: "Emotional sensitivity" },
  { key: "openness", label: "Openness" },
];
const CONFIDENCE = {
  early: { label: "Early signal", className: "border-amber-700/60 text-amber-300 bg-amber-950/30" },
  emerging: { label: "Emerging pattern", className: "border-sky-700/60 text-sky-300 bg-sky-950/30" },
  solid: { label: "Statistically useful", className: "border-emerald-700/60 text-emerald-300 bg-emerald-950/30" },
};
const TONE_ICON = { warning: AlertTriangle, good: CheckCircle2, neutral: Info };
const TONE_CLASS = {
  warning: "border-amber-800/60 bg-amber-950/20 text-amber-200",
  good: "border-emerald-800/60 bg-emerald-950/20 text-emerald-200",
  neutral: "border-slate-800 bg-slate-950/60 text-slate-300",
};

const num = (value) => (value == null ? "—" : value.toLocaleString());

const TraitRow = ({ label, survey, clinic, delta }) => (
  <div className="py-3 border-b border-slate-800 last:border-0" data-testid={`case-base-trait-${label.toLowerCase()}`}>
    <div className="flex items-baseline justify-between mb-2">
      <span className="text-slate-300 text-sm">{label}</span>
      <span className="text-xs text-slate-500">
        survey <span className="text-slate-300">{survey ?? "—"}</span>
        <span className="mx-2 text-slate-700">|</span>
        ours <span className="text-[#D4AF37]">{clinic ?? "—"}</span>
        {delta != null && Math.abs(delta) >= 0.05 && (
          <span className={`ml-2 ${delta > 0 ? "text-amber-400" : "text-emerald-400"}`}>
            {delta > 0 ? "+" : ""}
            {delta}
          </span>
        )}
      </span>
    </div>
    <div className="relative h-2 bg-slate-950 border border-slate-800">
      {survey != null && (
        <div className="absolute inset-y-0 left-0 bg-slate-600/70" style={{ width: `${(survey / 7) * 100}%` }} />
      )}
      {clinic != null && (
        <div className="absolute -bottom-1 w-[2px] h-4 bg-[#D4AF37]" style={{ left: `${(clinic / 7) * 100}%` }} />
      )}
    </div>
  </div>
);

const BehaviourList = ({ title, rows, denominator, accent }) => (
  <div className="border border-slate-800 bg-slate-950/60 p-5">
    <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">{title}</p>
    <p className="text-slate-600 text-[11px] mb-4">{denominator} coded case{denominator === 1 ? "" : "s"}</p>
    {rows.length === 0 && <p className="text-slate-500 text-sm">Nothing coded yet.</p>}
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.dimension}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-300">{row.label}</span>
            <span className="text-slate-500">{row.share}%</span>
          </div>
          <div className="h-1.5 bg-slate-900">
            <div className={accent} style={{ width: `${Math.min(100, row.share)}%`, height: "100%" }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const AdminCaseBaseTab = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    (window) => {
      setLoading(true);
      axios
        .get(`${API}/admin/case-base/insights?days=${window}`, authHeader())
        .then((res) => setData(res.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    },
    []
  );

  useEffect(() => {
    load(days);
  }, [days, load]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-3 text-slate-400 p-10" data-testid="case-base-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Comparing the case base…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="border border-slate-800 bg-slate-900/50 p-8 text-slate-400" data-testid="case-base-error">
        Could not load the case base right now.
      </div>
    );
  }

  const { baseline, clinic, deltas, growth, big_five: bigFive, confidence } = data;
  const conf = CONFIDENCE[confidence] || CONFIDENCE.early;
  const step = growth.length > 1 ? 100 / (growth.length - 1) : 0;
  const peak = Math.max(1, ...growth.map((p) => p.cumulative));
  const floor = Math.min(...growth.map((p) => p.cumulative));
  const span = Math.max(1, peak - floor);
  const points = growth
    .map((p, i) => `${(i * step).toFixed(2)},${(40 - ((p.cumulative - floor) / span) * 38).toFixed(2)}`)
    .join(" ");

  return (
    <div className="space-y-6" data-testid="case-base-tab">
      <div className="bg-slate-900/50 border border-slate-800 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
          <h2 className="font-serif text-xl text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-[#D4AF37]" />
            What our own case base shows
          </h2>
          <div className="flex items-center gap-2">
            <span
              className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1 ${conf.className}`}
              data-testid="case-base-confidence"
            >
              {conf.label}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => load(days)}
              data-testid="case-base-refresh"
              className="text-slate-400 hover:text-white"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <p className="text-slate-400 text-sm mb-6 max-w-3xl">
          The left-hand figure is always the imported {baseline.label} ({num(baseline.cases)} cases). The gold figure
          is what the clinic has collected itself from opted-in reports, intake forms and quizzes. Both are computed
          live from the same collection the matcher reads.
        </p>

        <div className="grid sm:grid-cols-4 gap-4">
          {[
            { label: "Cases in total", value: baseline.cases + clinic.cases, testid: "case-base-total" },
            { label: `${baseline.label} baseline`, value: baseline.cases, testid: "case-base-baseline" },
            { label: "Collected by the clinic", value: clinic.cases, testid: "case-base-clinic" },
            { label: "Added in the last 7 days", value: data.added_last_7_days, testid: "case-base-week" },
          ].map((item) => (
            <div key={item.label} className="border border-slate-800 bg-slate-950/60 p-4" data-testid={item.testid}>
              <p className="font-serif text-2xl text-[#D4AF37]">{num(item.value)}</p>
              <p className="text-slate-500 text-[10px] uppercase tracking-widest mt-1">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {data.callouts?.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="case-base-callouts">
          {data.callouts.map((callout, i) => {
            const Icon = TONE_ICON[callout.tone] || Info;
            return (
              <div
                key={callout.title}
                className={`border p-5 ${TONE_CLASS[callout.tone] || TONE_CLASS.neutral}`}
                data-testid={`case-base-callout-${i}`}
              >
                <p className="flex items-center gap-2 text-sm font-medium mb-2">
                  <Icon className="w-4 h-4 shrink-0" />
                  {callout.title}
                </p>
                <p className="text-slate-400 text-[13px] leading-relaxed">{callout.body}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="case-base-traits">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">Leader dark triad, 1–7</p>
          <p className="text-slate-500 text-[11px] mb-4">Grey bar = survey average · gold marker = our cases</p>
          {TRAITS.map((trait) => (
            <TraitRow
              key={trait.key}
              label={trait.label}
              survey={baseline.traits[trait.key]}
              clinic={clinic.traits[trait.key]}
              delta={deltas[trait.key]}
            />
          ))}
          <div className="mt-5 border border-slate-800 bg-slate-950/60 p-4" data-testid="case-base-high-share">
            <p className="text-slate-400 text-xs mb-1">Leaders rated ≥5/7 on narcissism AND Machiavellianism</p>
            <p className="text-sm">
              <span className="text-slate-300">{baseline.share_high_narc_mach ?? "—"}% survey</span>
              <span className="mx-2 text-slate-700">→</span>
              <span className="text-[#D4AF37]">{clinic.share_high_narc_mach ?? "—"}% ours</span>
            </p>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="case-base-growth-chart">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
            <p className="text-slate-300 text-sm">Case base growth</p>
          </div>
          <div className="flex gap-2 my-4">
            {[30, 90, 365].map((option) => (
              <button
                key={option}
                onClick={() => setDays(option)}
                data-testid={`case-base-days-${option}`}
                className={`text-[11px] px-3 py-1 border transition-colors ${
                  days === option
                    ? "border-[#D4AF37] text-[#D4AF37]"
                    : "border-slate-800 text-slate-500 hover:text-slate-300"
                }`}
              >
                {option === 365 ? "1 year" : `${option} days`}
              </button>
            ))}
          </div>
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-28 mb-2">
            <line x1="0" y1="39" x2="100" y2="39" stroke="#1F2937" strokeWidth="0.2" />
            <polyline points={points} fill="none" stroke="#D4AF37" strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="flex justify-between text-[10px] text-slate-500">
            <span>{growth[0]?.date}</span>
            <span>
              {num(growth[growth.length - 1]?.cumulative)} cumulative · {days}-day window
            </span>
            <span>{growth[growth.length - 1]?.date}</span>
          </div>

          <div className="mt-6 space-y-2">
            {Object.entries(data.by_source).map(([source, info]) => (
              <div
                key={source}
                className="flex items-center justify-between border border-slate-800 px-4 py-2.5"
                data-testid={`case-base-source-${source}`}
              >
                <span className="text-slate-400 text-xs">{info.label}</span>
                <span className="text-white text-sm">{num(info.cases)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <BehaviourList
          title={`Top behaviours — ${baseline.label}`}
          rows={baseline.top_behaviours}
          denominator={baseline.coded_behaviour_cases}
          accent="bg-slate-600"
        />
        <BehaviourList
          title="Top behaviours — our cases"
          rows={clinic.top_behaviours}
          denominator={clinic.coded_behaviour_cases}
          accent="bg-[#D4AF37]"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="case-base-big-five">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">
            The client's own profile — ours only
          </p>
          <p className="text-slate-500 text-[11px] mb-4">
            The survey never measured the respondent's personality. {bigFive.clients} of our cases do.
          </p>
          {Object.keys(bigFive.means || {}).length === 0 ? (
            <p className="text-slate-500 text-sm">No Big Five profiles contributed yet.</p>
          ) : (
            BIG_FIVE.filter((trait) => bigFive.means[trait.key] != null).map((trait) => (
              <div key={trait.key} className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-300">{trait.label}</span>
                  <span className="text-[#D4AF37]">{bigFive.means[trait.key]}/7</span>
                </div>
                <div className="h-1.5 bg-slate-950 border border-slate-800">
                  <div className="h-full bg-[#D4AF37]/70" style={{ width: `${(bigFive.means[trait.key] / 7) * 100}%` }} />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="case-base-geography">
          <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">
            <Globe className="w-3.5 h-3.5 text-[#D4AF37]" /> Geography
          </p>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="border border-slate-800 bg-slate-950/60 p-4">
              <p className="font-serif text-2xl text-slate-300">{num(baseline.countries)}</p>
              <p className="text-slate-500 text-[10px] uppercase tracking-widest mt-1">Survey countries</p>
            </div>
            <div className="border border-slate-800 bg-slate-950/60 p-4" data-testid="case-base-clinic-countries">
              <p className="font-serif text-2xl text-[#D4AF37]">{num(clinic.countries)}</p>
              <p className="text-slate-500 text-[10px] uppercase tracking-widest mt-1">Our countries</p>
            </div>
          </div>
          {data.new_countries?.length > 0 && (
            <p className="text-slate-400 text-xs mb-4" data-testid="case-base-new-countries">
              New to the evidence base: <span className="text-[#D4AF37]">{data.new_countries.join(", ")}</span>
            </p>
          )}
          <div className="space-y-2">
            {data.top_countries.map((row) => (
              <div key={row.country} className="flex items-center justify-between border border-slate-800 px-4 py-2">
                <span className="text-slate-400 text-xs">{row.country}</span>
                <span className="text-white text-sm">{row.cases}</span>
              </div>
            ))}
            {data.top_countries.length === 0 && (
              <p className="text-slate-500 text-sm">No country recorded on our cases yet.</p>
            )}
          </div>
          <p className="text-slate-600 text-[11px] mt-5" data-testid="case-base-optout">
            {data.opted_out_clients} client{data.opted_out_clients === 1 ? "" : "s"} opted out of research use.
          </p>
        </div>
      </div>
    </div>
  );
};
